import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';

// Material UI components
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Typography,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
  Badge,
} from '@mui/material';

// Material UI icons
import {
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  AccountCircle as ProfileIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationIcon,
  CreditCard as PaymentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

/**
 * Mobile bottom navigation component that appears only on small screens
 * Provides quick access to important app features
 */
const MobileBottomNavigation = ({ notifications = 0 }) => {
  const [value, setValue] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { primaryColor } = useThemeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  // Update active navigation item based on current path
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/') setValue(0);
    else if (path.includes('/dashboard')) setValue(1);
    else if (path.includes('/profile')) setValue(2);
    else if (path.includes('/subscription') || path.includes('/payment')) setValue(3);
  }, [location.pathname]);

  // Map of routes for bottom navigation actions
  const getRouteForValue = (val) => {
    switch (val) {
      case 0: return '/';
      case 1: return user?.role === 'admin' || user?.role === 'superadmin' 
        ? '/admin/dashboard' 
        : '/member/dashboard';
      case 2: return '/profile';
      case 3: return '/subscription';
      default: return '/';
    }
  };

  // Only render on mobile devices
  if (!isMobile) return null;

  // Ensure user object properties exist to prevent errors
  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}` : '';
  const userEmail = user?.email || '';
  
  // Render the mobile navigation
  return (
    <>
      <Paper sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1000,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: { xs: 'block', sm: 'none' }
        }} 
        elevation={3}
      >
        <BottomNavigation
          value={value}
          onChange={(event, newValue) => {
            if (newValue === 4) {
              // Menu button clicked
              handleMenuOpen();
            } else {
              setValue(newValue);
              navigate(getRouteForValue(newValue));
            }
          }}
          sx={{
            height: 60,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 0',
              color: 'text.secondary',
            },
            '& .Mui-selected': {
              color: primaryColor || 'primary.main',
            },
          }}
        >
          <BottomNavigationAction 
            label="Home" 
            icon={<HomeIcon />} 
          />
          {user && (
            <BottomNavigationAction 
              label="Dashboard" 
              icon={<DashboardIcon />} 
            />
          )}
          {user && (
            <BottomNavigationAction 
              label="Profile" 
              icon={<ProfileIcon />} 
            />
          )}
          {user && (
            <BottomNavigationAction 
              label="Subscription" 
              icon={<PaymentIcon />} 
            />
          )}
          <BottomNavigationAction 
            label="Menu" 
            icon={<MenuIcon />} 
          />
        </BottomNavigation>
      </Paper>

      {/* Menu drawer that slides up from bottom on mobile */}
      <Drawer
        anchor="bottom"
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Menu</Typography>
          <IconButton onClick={handleMenuClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider />
        
        <List sx={{ pt: 1, pb: 2 }}>
          {user ? (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/profile"
                  onClick={handleMenuClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <ProfileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`${user.firstName || ''} ${user.lastName || ''}`} 
                    secondary={user.email} 
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              </ListItem>
              
              <Divider sx={{ my: 1 }} />
              
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={user.role === 'admin' || user.role === 'superadmin' ? '/admin/dashboard' : '/member/dashboard'}
                  onClick={handleMenuClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/subscription"
                  onClick={handleMenuClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <PaymentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Subscriptions" />
                </ListItemButton>
              </ListItem>
              
              {user.role === 'admin' && (
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/admin/members"
                    onClick={handleMenuClose}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Badge badgeContent={notifications} color="error">
                        <NotificationIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="Notifications" />
                  </ListItemButton>
                </ListItem>
              )}
              
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/settings"
                  onClick={handleMenuClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                </ListItemButton>
              </ListItem>
              
              <Divider sx={{ my: 1 }} />
              
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <LogoutIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error.main' }} />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/login"
                  onClick={handleMenuClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <ProfileIcon />
                  </ListItemIcon>
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/register"
                  onClick={handleMenuClose}
                  sx={{ 
                    py: 1.5,
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: 1,
                    mx: 2,
                    my: 1,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  <ListItemText 
                    primary="Sign Up" 
                    sx={{ textAlign: 'center' }}
                  />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/register-tenant"
                  onClick={handleMenuClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Create Organization" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>

      {/* Add bottom padding to main content to prevent bottom navigation from covering content */}
      <Box sx={{ height: { xs: 60, sm: 0 }, mt: 0 }} />
    </>
  );
};

export default MobileBottomNavigation;